import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 27 (first half) — the ii-V-I and two rhythm patterns.
 *
 * Source: the lesson video (transcript in transcripts/piano-intermediate/
 * PI23_Rhythm_Patterns_Latin_Arpeggio.txt) and Jason's handwritten pages for
 * lesson 27. The video teaches the 2-5-1 in C (then F and G), voiced so the hand
 * barely moves — Dm7 D-F-A-C, G7 as D-F-G-B ("the D and F will be consistent"),
 * Cmaj7 C-E-G-B — and plays it in three rhythm patterns:
 *   1. Block chords, staccato, the last beat left ringing like an open hi-hat,
 *      over a Charleston left hand (1 and the "and" of 2) on root and fifth;
 *   2. A Latin rhythm (his chart: 1, 2, &3, &4 | &1, &2, 3, 4 — the V chord
 *      arriving early on the "and" of 4) over a pulse or a slow pulse;
 *   3. The La La Land arpeggio — the second half of this page.
 */

type PatternId = "block" | "latin";

/** ii7, V7, Imaj7 — RH voicings and LH root/fifth, in semitones above the tonic (C: Dm7, G7, Cmaj7). */
const CHORDS = [
  { degree: 2, suffix: "m7",   rh: [2, 5, 9, 12], root: -10, fifth: -3 },  // D-F-A-C over D
  { degree: 5, suffix: "7",    rh: [2, 5, 7, 11], root: -17, fifth: -10 }, // D-F-G-B over G
  { degree: 1, suffix: "maj7", rh: [0, 4, 7, 11], root: -12, fifth: -5 },  // C-E-G-B over C
];
/** Four bars: ii7 | V7 | Imaj7 | Imaj7 (index into CHORDS). */
const BARS = [0, 1, 2, 2];

/** Latin rhythm from Jason's chart: hit eighths in a two-bar cell. */
const LATIN_HITS = [0, 2, 5, 7, 9, 11, 12, 14];

export function LatinArpeggioSection() {
  return (
    <KeyProvider defaultTonic="C" availableQualities={["major"]}>
      <PIShell
        title="The ii-V-I — Blocks and the Latin Rhythm"
        exerciseGoal="The 2-5-1 in three keys, voiced so the hand barely moves, then played as staccato block chords and as a Latin rhythm."
        bpm={96}
        difficulty="Intermediate"
      >
        <Body />
      </PIShell>
    </KeyProvider>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [patternId, setPatternId] = useState<PatternId>("block");
  const [bpm, setBpm] = useState(96);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  const chordName = (ci: number) => k.nameForDegree(CHORDS[ci].degree) + CHORDS[ci].suffix;
  const name = (m: number) => midiToPitchName(m, k.useFlats);

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setRunning(false);
    setStep(-1);
  };

  useEffect(() => {
    if (running) setRhythmBpm(bpm, 0.05);
  }, [bpm, running]);

  useEffect(() => () => handleRef.current?.stop(), []);

  /** Which chord sounds at a given eighth of the four-bar loop. */
  const chordAt = (s: number): number => {
    const bar = Math.floor(s / 8);
    // Latin: the V7 arrives early, on the "and" of 4 of bar 1.
    if (patternId === "latin" && s === 7) return 1;
    return BARS[bar];
  };

  /** RH hit eighths across the 32-step loop for the current pattern. */
  const rhHits = (): number[] =>
    patternId === "block"
      ? [0, 1, 2, 3].flatMap((bar) => [0, 2, 4, 6].map((x) => bar * 8 + x))
      : [0, 16].flatMap((off) => LATIN_HITS.map((x) => off + x));

  const play = async () => {
    kickAudio();
    stop();
    const events: { beat: number; notes: string[]; durationSteps: number; velocity: number }[] = [];
    const hits = rhHits();
    hits.forEach((s, i) => {
      const ci = chordAt(s);
      const next = hits[i + 1] ?? 32;
      const isLastBeat = patternId === "block" && s % 8 === 6;
      events.push({
        beat: s,
        notes: CHORDS[ci].rh.map((o) => name(tonicMidi + o)),
        // Block chords are staccato except the last beat of the bar, which rings
        // ("the hi-hat open at the end of the bar"); Latin hits last until the next one.
        durationSteps: patternId === "block" ? (isLastBeat ? 2 : 0.5) : Math.max(1, next - s),
        velocity: isLastBeat ? 0.9 : 0.75,
      });
    });
    BARS.forEach((ci, bar) => {
      const c = CHORDS[ci];
      if (patternId === "block") {
        // Charleston: 1 and the "and" of 2 — root, then fifth.
        events.push({ beat: bar * 8, notes: [name(tonicMidi + c.root)], durationSteps: 3, velocity: 0.7 });
        events.push({ beat: bar * 8 + 3, notes: [name(tonicMidi + c.fifth)], durationSteps: 1, velocity: 0.65 });
      } else {
        // Slow pulse: beats 1 and 3.
        events.push({ beat: bar * 8, notes: [name(tonicMidi + c.root)], durationSteps: 4, velocity: 0.7 });
        events.push({ beat: bar * 8 + 4, notes: [name(tonicMidi + c.root)], durationSteps: 4, velocity: 0.6 });
      }
    });
    setRunning(true);
    handleRef.current = await playRhythm(events, {
      bpm,
      subdivide: 2,
      totalSteps: 32,
      loop: true,
      click: true,
      beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
  };

  const activeChord = step >= 0 ? chordAt(step) : 0;
  const highlights: KeyHighlight[] = CHORDS[activeChord].rh.map((o, i) => ({
    pc: ((k.tonicPc + o) % 12 + 12) % 12,
    tone: i === 0 ? "root" : "voice",
  }));
  const hits = new Set(rhHits());

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram highlights={highlights} octaves={2} startOctave={3} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          The keyboard shows the chord that is sounding. Jason plays this in C, then F
          (Gm7 – C7 – Fmaj7) and G (Am7 – D7 – Gmaj7) — pick them above.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The 2-5-1" title={`${chordName(0)} → ${chordName(1)} → ${chordName(2)}`}>
          <p>
            Take the major scale and build three of its seventh chords: the <strong>2</strong> is a
            minor 7th, the <strong>5</strong> a dominant 7th, the <strong>1</strong> a major 7th. The
            minor 7th is sort of stable, the dominant makes it unstable, and the major 7th feels the
            most at rest. In classical terms they are the pre-dominant, the dominant and the tonic.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "It just gives you that smooth flowing passage whenever you don't know where to go, you
            know, you could just do a 2 5 1."
          </PIQuote>
          <p>
            Play the 5 as written (root position) and the shift is weird and tough. So use an
            inversion: keep the {k.nameForDegree(2)} and {k.nameForDegree(4)} of the{" "}
            {chordName(0)} where they are and sneak in the rest —{" "}
            <strong>{CHORDS.map((c) => c.rh.map((o) => k.nameForPc(((k.tonicPc + o) % 12 + 12) % 12)).join("-")).join(" → ")}</strong>,
            with the root of each chord in the left hand. One bar each for the 2 and the 5, two
            bars on the 1.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Two rhythm patterns" title="Blocks, then the Latin rhythm">
          <div className="my-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            {(["block", "latin"] as PatternId[]).map((id) => (
              <button
                key={id}
                onClick={() => { stop(); setPatternId(id); }}
                className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                  id === patternId ? "border-amber-400/60 bg-amber-500/10" : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                }`}
              >
                <div className={`text-[14px] font-bold ${id === patternId ? "text-amber-200" : "text-cyan-100"}`}>
                  {id === "block" ? "Block chords" : "Latin rhythm"}
                </div>
                <div className="mt-1 text-[11px] leading-snug text-cyan-300/80">
                  {id === "block"
                    ? "RH chords on every beat, nice and staccato — think like a drummer and let the last one ring like an open hi-hat. LH Charleston on root and fifth."
                    : "RH hits on 1, 2, the \"and\" of 3, the \"and\" of 4 | the \"and\" of 1, the \"and\" of 2, 3, 4. LH keeps a slow pulse."}
                </div>
              </button>
            ))}
          </div>

          {/* Four bars of eighths; lit cells are the right-hand hits. */}
          <div className="my-3 space-y-1.5 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
            {BARS.map((ci, bar) => (
              <div key={bar} className="flex items-center gap-2">
                <span className="w-16 shrink-0 font-mono text-[11px] text-cyan-200">{chordName(ci)}</span>
                <div className="grid flex-1 gap-1" style={{ gridTemplateColumns: "repeat(8, minmax(0, 1fr))" }}>
                  {Array.from({ length: 8 }, (_, x) => {
                    const s = bar * 8 + x;
                    const isHit = hits.has(s);
                    const isNow = s === step;
                    const anticipates = patternId === "latin" && s === 7;
                    return (
                      <motion.div
                        key={x}
                        animate={{ scale: isNow ? 1.12 : 1 }}
                        transition={{ duration: 0.08 }}
                        className={`flex h-7 items-center justify-center rounded font-mono text-[9px] ${
                          isHit ? (anticipates ? "bg-rose-400 text-zinc-900" : "bg-amber-400 text-zinc-900") : "bg-zinc-800/60 text-cyan-300/50"
                        } ${isNow ? "ring-2 ring-white" : ""}`}
                      >
                        {x % 2 === 0 ? x / 2 + 1 : "&"}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
            {patternId === "latin" && (
              <p className="pt-1 text-[11px] text-rose-200/80">
                Rose cell: the {chordName(1)} arrives early, on the "and" of 4.
              </p>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={play}
              disabled={running}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
            >
              ▶ Loop the 2-5-1 — {patternId === "block" ? "block chords" : "Latin rhythm"}
            </button>
            {running && (
              <button
                onClick={stop}
                className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20"
              >
                ■ Stop
              </button>
            )}
            <label className="flex items-center gap-2 text-[12px] text-cyan-200">
              <span className="font-mono uppercase tracking-wider text-cyan-300/80">Tempo</span>
              <input
                type="range" min={60} max={130} step={1} value={bpm}
                onChange={(e) => setBpm(Number(e.target.value))}
                className="h-1 w-32 accent-amber-400"
              />
              <span className="w-10 font-mono text-[11px] text-cyan-100">{bpm}</span>
            </label>
          </div>
          <PIInsight>
            <p>
              The Charleston in the left hand — 1 and the "and" of 2 — is the swing rhythm from the
              blues lesson. Under the Latin rhythm, try a normal pulse first, then the slower pulse
              on 1 and 3.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Practice ladder" title="Fifteen minutes">
          <PracticeLadder
            intro={<>Chords first, rhythm second. Write the 2-5-1 down in as many keys as you can.</>}
            rungs={[
              { title: "The voicings", minutes: 4, accent: "amber", detail: "2-5-1 as held chords with the inversions above — the 2 and the 5 for a bar each, two bars on the 1. Roots in the left hand." },
              { title: "Block chords", minutes: 4, accent: "cyan", detail: "Staccato quarters, last beat ringing. Add the Charleston left hand on root and fifth." },
              { title: "Latin rhythm", minutes: 4, accent: "rose", detail: "1, 2, &3, &4 | &1, &2, 3, 4 — with the 5 arriving early. Left hand: pulse, then slow pulse." },
              { title: "F and G", minutes: 3, accent: "emerald", detail: "Gm7 – C7 – Fmaj7, then Am7 – D7 – Gmaj7, in both patterns." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Shared helpers ───────────────────────────────────────────────── */
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between"
      >
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            ✦ Study deck
          </div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ overflow: "hidden" }}
      >
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-cyan-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      {attribution && (
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}
